'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAppStore, Mode } from '@/store/useAppStore';
import { useServerHealthCheck } from '@/hooks/useServerHealthCheck';
import { useSwipeToDismiss } from '@/hooks/useSwipeToDismiss';
import {
  Server,
  Menu,
  X,
  SlidersHorizontal,
} from 'lucide-react';

// Navigation link
interface NavLink {
  path: string;
  label: string;
  mode: Mode;
}

// Category-grouped navigation
interface NavCategory {
  id: string;
  links: NavLink[];
}

// Links whose active state should match by prefix rather than exact pathname
// (used for routes with sub-paths like /comfyui/... and /short-story/<slug>/...)
const PREFIX_MATCH_PATHS = ['/comfyui', '/novel-generator/short-story'];

const isLinkActive = (linkPath: string, pathname: string | null | undefined): boolean => {
  if (!pathname) return false;
  if (PREFIX_MATCH_PATHS.includes(linkPath)) return pathname.startsWith(linkPath);
  return pathname === linkPath;
};

const navCategories: NavCategory[] = [
  {
    id: 'novel',
    links: [
      { path: '/novel', label: 'ノベルリーダー', mode: 'novel' },
      { path: '/novel-generator', label: '小説生成', mode: 'novel' },
      { path: '/novel-generator/characters', label: 'キャラクター', mode: 'novel' },
      { path: '/novel-generator/relations', label: '相関図', mode: 'novel' },
      { path: '/novel-generator/size', label: 'サイズ比較', mode: 'novel' },
      { path: '/novel-generator/cards', label: 'カード', mode: 'novel' },
      { path: '/novel-generator/solo', label: 'ソロ', mode: 'novel' },
      { path: '/novel-generator/maps', label: 'マップ', mode: 'novel' },
      { path: '/novel-generator/charasheet', label: 'キャラシート', mode: 'novel' },
      { path: '/novel-generator/ai-text', label: 'AIテキスト', mode: 'novel' },
      { path: '/novel-generator/short-story/story-init', label: '短編Studio', mode: 'novel' },
      { path: '/novel-generator/sound-novel', label: 'サウンドノベル', mode: 'novel' },
      { path: '/novel-generator/prompts', label: 'プロンプト', mode: 'novel' },
      { path: '/school', label: 'School', mode: 'novel' },
    ],
  },
  {
    id: 'image',
    links: [
      { path: '/eagle', label: 'Eagle', mode: 'i2i' },
      { path: '/comfyui', label: 'ComfyUI', mode: 'i2i' },
      { path: '/imagegen', label: '画像生成', mode: 'i2i' },
      { path: '/layer-editor', label: 'レイヤー編集', mode: 'i2i' },
      { path: '/magazine', label: 'マガジン', mode: 'i2i' },
      { path: '/image3d', label: '3D変換', mode: 'i2i' },
      { path: '/animate', label: '3Dアニメ', mode: 'i2i' },
      { path: '/rembg', label: '背景除去', mode: 'i2i' },
      { path: '/imagesearch', label: '画像検索', mode: 'i2i' },
      { path: '/video-downloader', label: '動画DL', mode: 'audio' },
    ],
  },
  {
    id: 'ai',
    links: [
      { path: '/text', label: 'チャット', mode: 'text' },
      { path: '/ocr', label: 'OCR', mode: 'ocr' },
    ],
  },
  {
    id: 'audio',
    links: [
      { path: '/audio', label: '文字起こし', mode: 'audio' },
      { path: '/audio-summary', label: '動画要約', mode: 'audio' },
      { path: '/audioextract', label: '音声抽出', mode: 'audio' },
      { path: '/audio-studio', label: '音声スタジオ', mode: 'audio' },
      { path: '/voice-call', label: '音声通話', mode: 'audio' },
      { path: '/music', label: '音楽生成', mode: 'audio' },
      { path: '/stable-audio', label: 'サウンド生成', mode: 'audio' },
    ],
  },
  {
    id: 'playground',
    links: [
      { path: '/playground', label: 'Playground', mode: 'i2i' },
    ],
  },
];


// Mobile: page sidebar toggle button (reads callback from store)
function PageSidebarToggle() {
  const openPageSidebar = useAppStore(s => s.openPageSidebar);
  if (!openPageSidebar) return null;
  return (
    <button
      onClick={openPageSidebar}
      className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
      aria-label="設定"
      title="ページ設定を開く"
    >
      <SlidersHorizontal className="w-4 h-4 text-[var(--text-muted)]" />
    </button>
  );
}

// Server configuration for control buttons
// `category` = ページ/機能ラベル, `name` = バックエンドのサービス名
// `customAction` が指定された場合は /api/servers の dedicated action を呼ぶ
type ManagedServer = {
  id: string;
  category: string;
  name: string;
  customAction?: { start: string; stop: string };
};

const MANAGED_SERVERS: ManagedServer[] = [
  { id: 'ocr', category: 'OCR', name: 'YomiToku' },
  { id: 'whisper', category: '文字起こし', name: 'Whisper' },
  { id: 'tts', category: '音声合成', name: 'Qwen3-TTS' },
  {
    id: 'bert-vits2',
    category: '音声合成',
    name: 'Bert-VITS2',
    customAction: { start: 'start-bert-vits2', stop: 'stop-bert-vits2' },
  },
  { id: 'irodori-tts', category: '音声合成', name: 'Irodori-TTS' },
  { id: 'depth', category: '3D変換', name: 'Depth Anything V2' },
  { id: 'rembg', category: '背景除去', name: 'Rembg' },
  { id: 'icrawler', category: '画像検索', name: 'icrawler' },
  { id: 'video-downloader', category: '動画DL', name: 'yt-dlp' },
  { id: 'ace-step', category: '音楽生成', name: 'ACE-Step' },
  { id: 'reforge', category: '画像生成', name: 'reForge' },
  { id: 'stable-audio', category: 'サウンド生成', name: 'Stable Audio' },
  { id: 'moss-sound-effect', category: 'サウンド生成', name: 'MOSS-SoundEffect' },
];

export default function Header() {
  const router = useRouter();
  const pathname = usePathname();
  const {
    mode,
    setMode,
    ollamaUrl,
    ocrServerUrl,
    whisperServerUrl,
    ocrServerStatus,
    ollamaServerStatus,
    whisperServerStatus,
    depthServerStatus,
    rembgServerStatus,
    qwenTtsServerStatus,
    icrawlerServerStatus,
    videoDownloaderServerStatus,
    setOcrServerStatus,
    setOllamaServerStatus,
    setWhisperServerStatus,
    setDepthServerStatus,
    setRembgServerStatus,
    setQwenTtsServerStatus,
    setIcrawlerServerStatus,
    setVideoDownloaderServerStatus,
    aceStepServerStatus,
    setAceStepServerStatus,
    stableAudioServerStatus,
    setStableAudioServerStatus,
    mossSoundEffectServerStatus,
    setMossSoundEffectServerStatus,
    bertVits2ServerStatus,
    setBertVits2ServerStatus,
    irodoriTtsServerStatus,
    setIrodoriTtsServerStatus,
    reforgeServerStatus,
    setReforgeServerStatus,
    comfyuiSelectedWorkflow,
  } = useAppStore();

  const [isServerControlOpen, setIsServerControlOpen] = useState(false);
  const { isMobileNavOpen: isMobileMenuOpen, setIsMobileNavOpen: setIsMobileMenuOpen } = useAppStore();
  const [serverActionLoading, setServerActionLoading] = useState<string | null>(null);
  const [comfyuiStatus, setComfyuiStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navScrollRef = useRef<HTMLElement>(null);

  // Swipe left to close mobile menu
  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);
  const { ref: menuSwipeRef, handlers: menuSwipeHandlers } = useSwipeToDismiss({
    direction: 'left',
    onDismiss: closeMobileMenu,
  });

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsServerControlOpen(false);
      }
    };
    if (isServerControlOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isServerControlOpen]);

  // Health checks for all servers
  useServerHealthCheck({
    url: ollamaUrl,
    endpoint: '/api/tags',
    onStatusChange: setOllamaServerStatus,
    currentStatus: ollamaServerStatus,
  });

  useServerHealthCheck({
    url: ocrServerUrl,
    endpoint: '/health',
    onStatusChange: setOcrServerStatus,
    currentStatus: ocrServerStatus,
  });

  useServerHealthCheck({
    url: whisperServerUrl,
    endpoint: '/health',
    onStatusChange: setWhisperServerStatus,
    currentStatus: whisperServerStatus,
  });

  useServerHealthCheck({
    url: 'http://localhost:8769',
    endpoint: '/health',
    onStatusChange: setDepthServerStatus,
    currentStatus: depthServerStatus,
  });

  useServerHealthCheck({
    url: 'http://localhost:8779',
    endpoint: '/health',
    onStatusChange: setRembgServerStatus,
    currentStatus: rembgServerStatus,
  });

  useServerHealthCheck({
    url: 'http://localhost:8771',
    endpoint: '/health',
    onStatusChange: setQwenTtsServerStatus,
    currentStatus: qwenTtsServerStatus,
  });

  useServerHealthCheck({
    url: 'http://localhost:8772',
    endpoint: '/health',
    onStatusChange: setIcrawlerServerStatus,
    currentStatus: icrawlerServerStatus,
  });

  useServerHealthCheck({
    url: 'http://localhost:8773',
    endpoint: '/health',
    onStatusChange: setVideoDownloaderServerStatus,
    currentStatus: videoDownloaderServerStatus,
  });

  useServerHealthCheck({
    url: 'http://localhost:8001',
    endpoint: '/health',
    onStatusChange: setAceStepServerStatus,
    currentStatus: aceStepServerStatus,
  });

  useServerHealthCheck({
    url: 'http://localhost:8774',
    endpoint: '/health',
    onStatusChange: setStableAudioServerStatus,
    currentStatus: stableAudioServerStatus,
  });

  useServerHealthCheck({
    url: 'http://localhost:8775',
    endpoint: '/health',
    onStatusChange: setMossSoundEffectServerStatus,
    currentStatus: mossSoundEffectServerStatus,
  });

  useServerHealthCheck({
    url: 'http://127.0.0.1:5000',
    endpoint: '/models/info',
    onStatusChange: setBertVits2ServerStatus,
    currentStatus: bertVits2ServerStatus,
  });

  // reForge: --api 有効時のみ /sdapi/v1/* が生えるので、それを health として使う
  useServerHealthCheck({
    url: 'http://127.0.0.1:7860',
    endpoint: '/sdapi/v1/sd-models',
    onStatusChange: setReforgeServerStatus,
    currentStatus: reforgeServerStatus,
  });

  useServerHealthCheck({
    url: 'http://localhost:8776',
    endpoint: '/health',
    onStatusChange: setIrodoriTtsServerStatus,
    currentStatus: irodoriTtsServerStatus,
  });

  // ComfyUI health check (via Next.js API to avoid CORS)
  const checkComfyUIStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/servers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'comfyui-status' }),
        signal: AbortSignal.timeout(5000),
      });
      const data = await response.json();
      setComfyuiStatus(data.status === 'online' ? 'online' : 'offline');
    } catch {
      setComfyuiStatus('offline');
    }
  }, []);

  useEffect(() => {
    checkComfyUIStatus();
    const interval = setInterval(checkComfyUIStatus, 15000);
    return () => clearInterval(interval);
  }, [checkComfyUIStatus]);

  // Server control functions
  const controlServer = useCallback(async (action: 'start' | 'stop', server: string) => {
    setServerActionLoading(`${action}-${server}`);
    try {
      const response = await fetch('/api/servers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, server }),
      });
      const result = await response.json();
      console.log(`Server ${action} result:`, result);
    } catch (error) {
      console.error(`Failed to ${action} server:`, error);
    } finally {
      setServerActionLoading(null);
    }
  }, []);

  // バックエンド単位の起動/停止 (Bert-VITS2 等の dedicated action にも対応)
  const controlBackend = useCallback(async (action: 'start' | 'stop', srv: ManagedServer) => {
    setServerActionLoading(`${action}-${srv.id}`);
    try {
      const body = srv.customAction
        ? { action: srv.customAction[action] }
        : { action, server: srv.id };
      const response = await fetch('/api/servers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      console.log(`Server ${action} result:`, result);
    } catch (error) {
      console.error(`Failed to ${action} ${srv.id}:`, error);
    } finally {
      setServerActionLoading(null);
    }
  }, []);

  // All ON/OFF (Bert-VITS2 は別アクションなので並行実行)
  const controlAll = useCallback(async (action: 'start' | 'stop') => {
    setServerActionLoading(`${action}-all`);
    try {
      await Promise.all([
        fetch('/api/servers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, server: 'all' }),
        }),
        fetch('/api/servers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: action === 'start' ? 'start-bert-vits2' : 'stop-bert-vits2' }),
        }),
      ]);
    } catch (error) {
      console.error(`Failed to ${action} all:`, error);
    } finally {
      setServerActionLoading(null);
    }
  }, []);

  // ComfyUI control (via Next.js API)
  const controlOllama = useCallback(async (action: 'start' | 'stop') => {
    setServerActionLoading(`${action}-ollama`);
    try {
      const response = await fetch('/api/servers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: `${action}-ollama` }),
        signal: AbortSignal.timeout(60000),
      });
      const result = await response.json();
      console.log(`Ollama ${action} result:`, result);
    } catch (error) {
      console.error(`Failed to ${action} Ollama:`, error);
      alert(`Ollama ${action === 'start' ? '起動' : '停止'}に失敗しました。`);
    } finally {
      setServerActionLoading(null);
    }
  }, []);

  const controlComfyUI = useCallback(async (action: 'start' | 'stop') => {
    setServerActionLoading(`${action}-comfyui`);
    try {
      const response = await fetch('/api/servers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: `${action}-comfyui` }),
        signal: AbortSignal.timeout(120000), // 2 minute timeout for startup
      });
      const result = await response.json();
      console.log(`ComfyUI ${action} result:`, result);
      // Refresh status after action
      setTimeout(checkComfyUIStatus, 3000);
    } catch (error) {
      console.error(`Failed to ${action} ComfyUI:`, error);
      alert(`ComfyUI ${action === 'start' ? '起動' : '停止'}に失敗しました。`);
    } finally {
      setServerActionLoading(null);
    }
  }, [checkComfyUIStatus]);

  const getServerStatus = useCallback((serverId: string) => {
    switch (serverId) {
      case 'ocr': return ocrServerStatus;
      case 'whisper': return whisperServerStatus;
      case 'tts': return qwenTtsServerStatus;
      case 'bert-vits2': return bertVits2ServerStatus;
      case 'irodori-tts': return irodoriTtsServerStatus;
      case 'depth': return depthServerStatus;
      case 'rembg': return rembgServerStatus;
      case 'icrawler': return icrawlerServerStatus;
      case 'video-downloader': return videoDownloaderServerStatus;
      case 'ace-step': return aceStepServerStatus;
      case 'stable-audio': return stableAudioServerStatus;
      case 'moss-sound-effect': return mossSoundEffectServerStatus;
      case 'reforge': return reforgeServerStatus;
      default: return 'offline';
    }
  }, [ocrServerStatus, whisperServerStatus, qwenTtsServerStatus, bertVits2ServerStatus, irodoriTtsServerStatus, depthServerStatus, rembgServerStatus, icrawlerServerStatus, videoDownloaderServerStatus, aceStepServerStatus, stableAudioServerStatus, mossSoundEffectServerStatus, reforgeServerStatus]);

  // Count online servers for badge
  // Ollama + (managed servers) + ComfyUI
  const allStatuses = [
    ollamaServerStatus,
    ocrServerStatus,
    whisperServerStatus,
    qwenTtsServerStatus,
    bertVits2ServerStatus,
    irodoriTtsServerStatus,
    depthServerStatus,
    rembgServerStatus,
    icrawlerServerStatus,
    videoDownloaderServerStatus,
    aceStepServerStatus,
    stableAudioServerStatus,
    mossSoundEffectServerStatus,
    reforgeServerStatus,
    comfyuiStatus,
  ];
  const totalCount = allStatuses.length;
  const onlineCount = allStatuses.filter(s => s === 'online').length;

  // Shared server dropdown content
  const serverDropdownContent = (
    <div className="bg-white rounded-lg shadow-lg border border-[var(--border)]">
      {/* Header with All ON/OFF */}
      <div className="p-2 border-b border-[var(--border)] flex gap-1">
        <button
          onClick={() => controlAll('start')}
          disabled={serverActionLoading !== null}
          className="flex-1 px-2 py-1 text-xs text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
        >
          All ON
        </button>
        <button
          onClick={() => controlAll('stop')}
          disabled={serverActionLoading !== null}
          className="flex-1 px-2 py-1 text-xs text-white bg-red-500 rounded hover:bg-red-600 disabled:opacity-50"
        >
          All OFF
        </button>
      </div>

      <div className="p-1">
        {/* Ollama - Start/Stop via /api/servers (spawn `ollama serve` on host) */}
        <div className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ollamaServerStatus === 'online' ? 'bg-green-500' : 'bg-red-400'}`} />
            <span className="text-xs text-[var(--text-muted)]">システム</span>
            <span className="text-xs text-gray-300">/</span>
            <span className="text-xs font-medium truncate">Ollama</span>
          </div>
          <button
            onClick={() => controlOllama(ollamaServerStatus === 'online' ? 'stop' : 'start')}
            disabled={serverActionLoading !== null}
            className={`px-2 py-0.5 text-[10px] rounded flex-shrink-0 ml-2 ${
              ollamaServerStatus === 'online' ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
            } disabled:opacity-50`}
          >
            {serverActionLoading?.includes('ollama') ? '...' : ollamaServerStatus === 'online' ? 'Stop' : 'Start'}
          </button>
        </div>

        {/* Managed servers */}
        {MANAGED_SERVERS.map((server, idx) => {
          const status = getServerStatus(server.id);
          const isOnline = status === 'online';
          const isLoading = serverActionLoading?.includes(server.id);
          // 同じカテゴリが連続する場合はカテゴリラベルを淡く表示
          const prevSameCategory = idx > 0 && MANAGED_SERVERS[idx - 1].category === server.category;
          return (
            <div key={server.id} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-green-500' : 'bg-red-400'}`} />
                <span className={`text-xs flex-shrink-0 ${prevSameCategory ? 'text-gray-300' : 'text-[var(--text-muted)]'}`}>
                  {server.category}
                </span>
                <span className="text-xs text-gray-300 flex-shrink-0">/</span>
                <span className="text-xs font-medium truncate">{server.name}</span>
              </div>
              <button
                onClick={() => controlBackend(isOnline ? 'stop' : 'start', server)}
                disabled={isLoading || serverActionLoading !== null}
                className={`px-2 py-0.5 text-[10px] rounded flex-shrink-0 ml-2 ${
                  isOnline ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
                } disabled:opacity-50`}
              >
                {isLoading ? '...' : isOnline ? 'Stop' : 'Start'}
              </button>
            </div>
          );
        })}

        {/* ComfyUI */}
        <div className="border-t border-gray-100 mt-1 pt-1">
          <div className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${comfyuiStatus === 'online' ? 'bg-green-500' : 'bg-red-400'}`} />
              <span className="text-xs text-[var(--text-muted)] flex-shrink-0">画像生成</span>
              <span className="text-xs text-gray-300 flex-shrink-0">/</span>
              <span className="text-xs font-medium truncate">ComfyUI</span>
            </div>
            <button
              onClick={() => controlComfyUI(comfyuiStatus === 'online' ? 'stop' : 'start')}
              disabled={serverActionLoading?.includes('comfyui') || serverActionLoading !== null}
              className={`px-2 py-0.5 text-[10px] rounded flex-shrink-0 ml-2 ${
                comfyuiStatus === 'online' ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
              } disabled:opacity-50`}
            >
              {serverActionLoading?.includes('comfyui') ? '...' : comfyuiStatus === 'online' ? 'Stop' : 'Start'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Find active nav link label for mobile display
  const activeLink = navCategories.flatMap(c => c.links).find(link =>
    isLinkActive(link.path, pathname)
  );

  return (
    <>
      {/* 外側 = safe-area 吸収用ラッパー (ホーム画面アプリでの上端隠れ対策)。
          内側の <header> は h-10 = 40px 固定で、中身が増えても高さは変わらない。 */}
      <div className="app-safe-top flex-shrink-0 bg-white">
      <header className="h-10 flex items-center justify-between px-4 bg-white border-b border-[var(--border)]">
        <div className="flex items-center gap-3 min-w-0">
          {/* Mobile hamburger button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
            aria-label="メニュー"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <h1
            onClick={() => router.push('/')}
            className="text-sm font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
          >
            Local AI Studio
          </h1>

          {/* Mobile: show active page name */}
          {activeLink && (
            <span className="md:hidden text-xs text-[var(--accent)] font-medium truncate">
              {activeLink.label}
            </span>
          )}

          {/* Server Control - Icon next to title */}
          <div className="relative hidden md:block" ref={dropdownRef}>
            <button
              onClick={() => setIsServerControlOpen(!isServerControlOpen)}
              className={`p-1.5 rounded-lg transition-colors relative ${
                isServerControlOpen ? 'bg-slate-100' : 'hover:bg-slate-50'
              }`}
              title="サーバー管理"
            >
              <Server className="w-4 h-4 text-[var(--text-muted)]" />
              {/* Online count badge */}
              <span className={`absolute -top-0.5 -right-0.5 w-3.5 h-3.5 text-[8px] font-bold rounded-full flex items-center justify-center ${
                onlineCount === totalCount ? 'bg-green-500 text-white' : onlineCount > 0 ? 'bg-yellow-500 text-white' : 'bg-red-400 text-white'
              }`}>
                {onlineCount}
              </span>
            </button>

            {isServerControlOpen && (
              <div className="absolute left-0 top-full mt-1 w-96 z-50">
                {serverDropdownContent}
              </div>
            )}
          </div>

          {/* Desktop nav */}
          <nav
            className="hidden md:flex items-center flex-nowrap overflow-x-auto"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            ref={navScrollRef}
            onWheel={(e) => {
              if (navScrollRef.current) {
                e.preventDefault();
                navScrollRef.current.scrollLeft += e.deltaY;
              }
            }}
          >
            {navCategories.map((category, catIndex) => (
              <div key={category.id} className="flex items-center flex-shrink-0">
                {catIndex > 0 && (
                  <div className="w-px h-4 bg-gray-200 mx-1 flex-shrink-0" />
                )}
                {category.links.map((link) => {
                  const isActive = isLinkActive(link.path, pathname);
                  return (
                    <button
                      key={link.path}
                      onClick={() => {
                        setMode(link.mode);
                        router.push(link.path);
                      }}
                      className={`px-2 py-1 text-xs whitespace-nowrap transition-colors flex-shrink-0 ${
                        isActive
                          ? 'text-[var(--accent)] font-medium'
                          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      {link.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* ComfyUI selected workflow info (only on main workflow page) */}
          {pathname === '/comfyui' && comfyuiSelectedWorkflow && (
            <div className="hidden md:flex items-center gap-2 ml-2 px-2 py-1 bg-[var(--bg-tertiary)] rounded flex-shrink-0">
              <span className="text-[10px] text-[var(--text-muted)]">
                {comfyuiSelectedWorkflow.category}
              </span>
              <span className="text-xs font-medium text-[var(--text-primary)]">
                {comfyuiSelectedWorkflow.name}
              </span>
            </div>
          )}
        </div>
      </header>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Menu panel */}
          <div
            className="app-safe-top safe-area-bottom relative w-64 max-w-[80vw] bg-white h-full overflow-y-auto shadow-xl flex flex-col"
            ref={(el) => { menuSwipeRef.current = el; }}
            {...menuSwipeHandlers}
          >
            {/* Menu header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
              <span className="text-sm font-semibold text-[var(--text-primary)]">メニュー</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1 rounded hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation links */}
            <div className="flex-1 overflow-y-auto py-2">
              {navCategories.map((category, catIndex) => (
                <div key={category.id}>
                  {catIndex > 0 && (
                    <div className="h-px bg-gray-200 mx-4 my-1" />
                  )}
                  {category.links.map((link) => {
                    const isActive = isLinkActive(link.path, pathname);
                    return (
                      <button
                        key={link.path}
                        onClick={() => {
                          setMode(link.mode);
                          router.push(link.path);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                          isActive
                            ? 'text-[var(--accent)] font-medium bg-blue-50'
                            : 'text-[var(--text-secondary)] hover:bg-slate-50 hover:text-[var(--text-primary)]'
                        }`}
                      >
                        {link.label}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Server control in mobile menu */}
            <div className="border-t border-[var(--border)] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Server className="w-4 h-4 text-[var(--text-muted)]" />
                <span className="text-xs font-medium text-[var(--text-secondary)]">サーバー管理</span>
                <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  onlineCount === totalCount ? 'bg-green-100 text-green-700' : onlineCount > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                }`}>
                  {onlineCount}/{totalCount}
                </span>
              </div>
              {serverDropdownContent}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
